import axios, { AxiosResponse } from 'axios';
import { mapKeys, snakeCase } from 'lodash';
import { logger } from '@fiverr-private/obs';
import pathfinder from '@fiverr-private/pathfinder';
import { DmBriefStatus, DmMatchCandidateRejectReason, DmMatchCandidateStatus } from '../gql-operations/gql-generated';
import { SellerRejectAdditionalInfo } from '../components/SellerBriefDeclineDropdown/types';
import type { AgenticMatchCandidateRejectReason } from '../components/SellerBriefDeclineModal';

interface DeclineMatchCandidateRequest {
    id: string;
    reason: DmMatchCandidateRejectReason;
    rejectAdditionalInfo?: SellerRejectAdditionalInfo;
}

interface DeclineAgenticMatchCandidateRequest {
    id: string;
    rejectReasonTypes: AgenticMatchCandidateRejectReason[];
    rejectReasonText?: string;
}

interface UpdateMatchCandidateRequest {
    id: string;
    status: DmMatchCandidateStatus;
    reject_reason: DmMatchCandidateRejectReason;
    reject_additional_info?: SellerRejectAdditionalInfo;
}

export const declineMatchCandidate = async ({ id, reason, rejectAdditionalInfo }: DeclineMatchCandidateRequest) =>
    updateMatchCandidate({
        id,
        reject_reason: reason,
        status: DmMatchCandidateStatus.ClosedRejectedBySeller,
        reject_additional_info: rejectAdditionalInfo && mapKeys(rejectAdditionalInfo, (_, key) => snakeCase(key)),
    });

export const declineAgenticMatchCandidate = async ({
    id,
    rejectReasonTypes,
    rejectReasonText,
}: DeclineAgenticMatchCandidateRequest) => {
    const url = pathfinder('agentic_brief_update_match_candidate', {
        match_candidate_id: id,
    });

    try {
        await axios.patch(url, {
            status: DmMatchCandidateStatus.ClosedRejectedBySeller,
            reject_reason_types: rejectReasonTypes,
            ...(rejectReasonText ? { reject_reason_text: rejectReasonText } : {}),
        });
    } catch (error) {
        logger.error(`Failed to reject agentic Match Candidate: ${id}`, { error });
        throw error;
    }
};

export const updateMatchCandidate = async ({ id, ...params }: UpdateMatchCandidateRequest) => {
    const url = pathfinder('update_dm_match_candidate', {
        match_candidate_id: id,
    });

    try {
        await axios.patch<UpdateMatchCandidateRequest>(url, {
            ...params,
        });
    } catch (error) {
        logger.error(`Failed to update Match Candidate: ${id}`, { error });
        throw error;
    }
};

export const changeBriefStatus = async ({
    briefId,
    reason,
    status,
}: {
    briefId: string;
    reason?: string;
    status: DmBriefStatus;
}): Promise<AxiosResponse> => {
    try {
        return axios.patch(pathfinder('update_dm_brief', { brief_id: briefId }), {
            status,
            close_reason: reason,
        });
    } catch (error: unknown) {
        logger.error(`Failed to change brief status for brief id ${briefId} and reason ${reason}`, {
            error,
        });
        throw error;
    }
};
